import { CountryId } from '@/entities/country';
import { DepartureCityId } from '@/entities/departureCity';
import { OperatorId } from '@/entities/operator';
import type { SearchCriteria, Selection } from '@/entities/searchCriteria';
import type { Country, DepartureCity } from '@/shared/model';
import type { FiltersModel, SearchCriteriaFlagGroupId } from '../model/types';

export const EMIRATES_FILTER_GROUP_ID: SearchCriteriaFlagGroupId = 'block';

export const EMIRATES_COUNTRY_ID = CountryId.Uae;

export const EMIRATES_DEFAULT_DEPARTURE_CITY_ID = DepartureCityId.Moscow;

const EMIRATES_DEPARTURE_CITY_IDS: ReadonlySet<number> = new Set([
  DepartureCityId.Moscow,
  DepartureCityId.SaintPetersburg,
]);

export const EMIRATES_OPERATOR_KEYS: readonly string[] = OperatorId.Travelata.map(String);

type EmiratesDirection = Pick<SearchCriteria, 'destination'> & Partial<Pick<SearchCriteria, 'fromCity'>>;

export interface EmiratesDirectionSource {
  country: Country | null;
  city: DepartureCity | null;
}

function isEmiratesDepartureCity(city: DepartureCity | null | undefined): city is DepartureCity {
  return !!city && EMIRATES_DEPARTURE_CITY_IDS.has(city.id);
}

export function isEmiratesDestination(destination: Selection | null): boolean {
  return !!destination && destination.kind !== 'none' && destination.country.id === EMIRATES_COUNTRY_ID;
}

export function isEmiratesDirection({ fromCity, destination }: EmiratesDirection): boolean {
  return isEmiratesDepartureCity(fromCity) && isEmiratesDestination(destination);
}

export function withAvailableEmirates(criteria: SearchCriteria): SearchCriteria {
  if (!criteria.block || isEmiratesDirection(criteria)) return criteria;

  return { ...criteria, block: undefined };
}

export function withEmiratesDirection(
  criteria: SearchCriteria,
  { country, city }: EmiratesDirectionSource,
): SearchCriteria | null {
  const fromCity = isEmiratesDepartureCity(criteria.fromCity) ? criteria.fromCity : city;
  const destination: Selection | null = isEmiratesDestination(criteria.destination)
    ? criteria.destination
    : country && { kind: 'country', country };

  if (!fromCity || !destination) return null;

  return { ...criteria, fromCity, destination, block: true };
}

export function withEmiratesOperators(filtersModel: FiltersModel): FiltersModel {
  const missingKeys = EMIRATES_OPERATOR_KEYS.filter((key) => !filtersModel.operators.includes(key));

  if (!missingKeys.length) return filtersModel;

  return { ...filtersModel, operators: [...filtersModel.operators, ...missingKeys] };
}

export function withoutEmiratesOperators(filtersModel: FiltersModel): FiltersModel {
  const operators = filtersModel.operators.filter((key) => !EMIRATES_OPERATOR_KEYS.includes(key));

  if (operators.length === filtersModel.operators.length) return filtersModel;

  return { ...filtersModel, operators };
}
